import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';

import Dashboard from './pages/Dashboard';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminProjects from './pages/AdminProjects';
import AdminPlots from './pages/AdminPlots';
import AdminAudit from './pages/AdminAudit';
import AdminAssociates from './pages/AdminAssociates';
import AssociateDashboard from './pages/AssociateDashboard';
import TeamTree from './pages/TeamTree';
import AdminHierarchy from './pages/AdminHierarchy';
import AdminLayout from './components/layout/AdminLayout';
import { SALES_ROLES, isSalesRole } from './utils/roles';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30000, retry: 1 } }
});

function ProtectedRoute({ children, allowRoles }) {
  const { admin, loading } = useAuth();
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f4f6f8' }}>
      <div className="spinner" />
    </div>
  );
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (allowRoles && !allowRoles.includes(admin.role)) {
    // Wrong role for this area — redirect to their own home
    const home = isSalesRole(admin.role) ? '/associate' : '/admin';
    return <Navigate to={home} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Dashboard />} />

      {/* Shared login for admin/sales-team */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Sales-team read-only inventory portal (CLM, BDO, Sales Rep, Associate) */}
      <Route path="/associate" element={
        <ProtectedRoute allowRoles={SALES_ROLES}>
          <AssociateDashboard />
        </ProtectedRoute>
      } />

      {/* Sales-team: their own recruitment tree + ability to recruit new team members */}
      <Route path="/team" element={
        <ProtectedRoute allowRoles={SALES_ROLES}>
          <TeamTree />
        </ProtectedRoute>
      } />

      {/* Admin / Superadmin management area */}
      <Route path="/admin" element={
        <ProtectedRoute allowRoles={['admin', 'superadmin']}>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="projects" element={<AdminProjects />} />
        <Route path="projects/:projectId/plots" element={<AdminPlots />} />
        <Route path="associates" element={<AdminAssociates />} />
        <Route path="hierarchy" element={<AdminHierarchy />} />
        <Route path="audit" element={<AdminAudit />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { fontFamily: 'DM Sans, sans-serif', fontSize: '13px' },
              success: { iconTheme: { primary: '#1D9E75', secondary: '#fff' } }
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
